import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { AlertifyService } from '../../services/alertify.service';
import { PartnerContactService } from '../../services/partner-contact.service';
import { Project, ProjectSurveyPartner, ProjectTeam } from '../../models/project';
import { ProjectService } from '../../services/project.service';
import { EmailService } from 'projects/core/src/services/email.service';

export interface VendorContactOption {
  name: string;
  value: string;
  email?: string;
  partnerId?: string;
}

export abstract class BaseNotifyComponent {
  protected _project: Project;
  
  get project(): Project {
    return this._project;
  }
  set project(value: Project) {
    this._project = value;
    this.projectTeam = value?.projectTeam || [];
    this.loadPrecomputedLists();
  }

  vendorList: string[] = [];
  noteContent: string = '';
  projectTeam: ProjectTeam[] = [];

  precomputedContactLists: VendorContactOption[][] = [];
  precomputedSelectedTeams: VendorContactOption[][] = [];

  editorConfig = {
    startupFocus: true,
    language: 'en',
    placeholder: "Type body and click save",
    toolbar: {
      items: [
        'bold',
        'italic',
        'link',
        'bulletedList',
        'numberedList'
      ],
    }
  };

  selectedUsers: VendorContactOption[] = [];
  emailSubject: string = '';
  emailTemplate: string = '';

  constructor(
    public bsModalRef: BsModalRef,
    public modalService: BsModalService,
    protected projectService: ProjectService,
    protected alertify: AlertifyService,
    protected emailService: EmailService,
    protected partnerContactService: PartnerContactService
  ) { }

  initialize() {
    this.loadPrecomputedLists();
    this.loadEmailContent();
  }

  protected fetchProjectTeamMembersForPartners(partnerIds: string[]) {
    this.projectService.getProjectTeamMembersForPartners(this.project.id, partnerIds).subscribe(teamMembers => {
      this.selectedUsers = teamMembers.filter(user => user.email && user.email.length > 0).map(member => ({
        name: member.displayName,
        value: member.email,
        email: member.email,
        partnerId: member.partnerId
      }));
      this.updateComputedSelectedTeams();
    }, error => {
      console.error('Error fetching project team members:', error);
      this.alertify.error('Failed to load team members.');
    });
  }

  protected loadPrecomputedLists() {
    if (!this.vendorList || this.vendorList.length === 0) {
      return;
    }

    this.precomputedContactLists = Array(this.vendorList.length).fill([]);

    let loadedCount = 0;
    const partnerIds = this.vendorList;
    this.vendorList.forEach((vendor, idx) => {
      this.partnerContactService.GetPartnerContacts(vendor).subscribe(data => {
        this.precomputedContactLists[idx] = data.map(contact => ({
          name: contact.name,
          value: contact.email,
          email: contact.email,
          partnerId: contact.vendorId
        }));
        loadedCount++;
        if (loadedCount === this.vendorList.length) {
          this.fetchProjectTeamMembersForPartners(partnerIds);
        }
      }, error => {
        this.precomputedContactLists[idx] = [];
        loadedCount++;
        if (loadedCount === this.vendorList.length) {
          this.fetchProjectTeamMembersForPartners(partnerIds);
        }
        console.error(`Error fetching contacts for vendor ${vendor}:`, error);
      });
    });
  }

  protected abstract loadEmailContent(): void;

  protected updateComputedSelectedTeams() {
    this.precomputedSelectedTeams = this.vendorList.map(vendor =>
      this.selectedUsers.filter(user => user['partnerId'] === vendor)
    );
  }

  addUser(newUser: VendorContactOption) {
    this.selectedUsers = [...this.selectedUsers, newUser];
    this.updateComputedSelectedTeams();
  }

  updateUser(removedUser: VendorContactOption) {
    this.selectedUsers = this.selectedUsers.filter(user => user.value !== removedUser.value);
    this.updateComputedSelectedTeams();
  }

  closeModal() {
    this.bsModalRef.hide();
  }

  notifyWithEmail() {
    if (this.emailTemplate.trim() === '') {
      this.alertify.error('Please write a note.');
      return;
    }

    if (this.selectedUsers.length === 0) {
      this.alertify.error('Please select at least one user to notify.');
      return;
    }

    // Use the existing precomputedSelectedTeams structure which is already separated by vendors
    const vendorGroups = this.precomputedSelectedTeams.filter(team => team.length > 0);
    
    if (vendorGroups.length === 0) {
      this.alertify.error('No valid vendor contacts selected.');
      return;
    }

    // Send separate emails for each vendor group
    let emailsSent = 0;
    let totalEmails = vendorGroups.length;
    let hasErrors = false;

    vendorGroups.forEach((contacts, index) => {
      const tos = contacts.map(contact => contact.email).filter(email => email);
      
      if (tos.length === 0) {
        totalEmails--;
        return;
      }

      // Use the overridable sendEmail method
      this.sendEmail({
        from: 'navigator@navigatorsurveys.com',
        to: tos,
        subject: this.emailSubject,
        body: this.emailTemplate
      }, contacts[0].partnerId).subscribe(response => {
        emailsSent++;
        if (emailsSent === totalEmails) {
          this.bsModalRef.hide();
          if (hasErrors) {
            this.alertify.error(`Notification emails sent to ${emailsSent} vendor(s). Some emails failed to send.`);
          } else {
            this.alertify.success(`Notification emails sent to ${emailsSent} vendor(s) successfully.`);
          }
        }
      }, error => {
        hasErrors = true;
        emailsSent++;
        console.error(`Error sending email to vendor:`, error);
        
        if (emailsSent === totalEmails) {
          this.bsModalRef.hide();
          if (hasErrors) {
            this.alertify.error(`Notification emails sent to ${emailsSent} vendor(s). Some emails failed to send.`);
          } else {
            this.alertify.success(`Notification emails sent to ${emailsSent} vendor(s) successfully.`);
          }
        }
      });
    });
  }

  /**
   * Overridable method for sending email. By default, uses emailService.sendEmail.
   * Subclasses can override this to swap out the email sending implementation.
   */
  protected sendEmail(emailPayload: { from: string; to: string[]; subject: string; body: string; }, vendorId: string) {
    return this.emailService.sendEmail(emailPayload);
  }

  cancel() {
    this.bsModalRef.hide();
  }

  wysiwygUpdate(event) {
    this.emailTemplate = event;
  }
}
